import {
  PathPickBasketSortOption,
  PathPickBasketTab,
} from "@/features/pathpick/features/basket/types";
import { PathPickProductOrderBy } from "@/features/pathpick/types";

// 탭별 정렬 옵션 — 각 배열의 첫 값이 그 탭의 디폴트다.
// 탭마다 기준 시각(담기/신청/주문)이 달라 옵션 자체가 다르므로 탭 전환 시 정렬은 초기화된다.
export const BASKET_SORT_OPTIONS_BY_TAB: Record<
  PathPickBasketTab,
  PathPickBasketSortOption[]
> = {
  pending: [
    "recentlyAdded",
    "kreamMarginDesc",
    "poizonMarginDesc",
    "kreamSalesDesc",
    "poizonSalesDesc",
  ],
  requested: [
    "recentlyRequested",
    "kreamMarginDesc",
    "poizonMarginDesc",
    "kreamSalesDesc",
    "poizonSalesDesc",
  ],
  ordered: [
    "recentlyOrdered",
    "kreamMarginDesc",
    "poizonMarginDesc",
    "kreamSalesDesc",
    "poizonSalesDesc",
  ],
};

// 비교 표시를 끄면 감추는 정렬 — 카드에 없는 값을 기준으로 줄을 세우게 두지 않는다.
// (마진율도 거래수도 카드의 비교 블록에만 그려진다)
export const BASKET_COMPARISON_SORT_OPTIONS: PathPickBasketSortOption[] = [
  "kreamMarginDesc",
  "poizonMarginDesc",
  "kreamSalesDesc",
  "poizonSalesDesc",
];

// 비교 기준(입찰가/거래가)이 바뀌면 순서가 재배치되는 정렬 — 그때만 첫 페이지로 되돌린다.
// 거래순은 체결건수/판매량 자체로 줄을 세워 기준과 무관하므로 위 목록과 따로 둔다.
export const BASKET_MARGIN_SORT_OPTIONS: PathPickBasketSortOption[] = [
  "kreamMarginDesc",
  "poizonMarginDesc",
];

// 정렬 값 → i18n 키
export const BASKET_SORT_LABEL_KEYS: Record<PathPickBasketSortOption, string> =
  {
    recentlyAdded: "path_pick:basket.list.sort_recently_added",
    recentlyRequested: "path_pick:basket.list.sort_recently_requested",
    recentlyOrdered: "path_pick:basket.list.sort_recently_ordered",
    kreamMarginDesc: "path_pick:basket.list.sort_kream_margin_desc",
    poizonMarginDesc: "path_pick:basket.list.sort_poizon_margin_desc",
    kreamSalesDesc: "path_pick:basket.list.sort_kream_sales_desc",
    poizonSalesDesc: "path_pick:basket.list.sort_poizon_sales_desc",
  };

// 정렬 값 → order_by 파라미터.
// recent_* 세 값 모두 "이 바이어 기준" 시각이라 탭별 기준(담기/신청/주문)과 1:1로 대응한다.
// 마진순은 마진 계산의 기준 가격을 market_price_basis(비교 기준)가 정한다.
// 거래순은 크림 누적 체결건수 / 포이즌 최근 30일 판매량 많은 순이고, 그 마켓에
// 매칭되지 않은 상품은 뒤로 밀린다.
export const BASKET_SORT_ORDER_BY: Record<
  PathPickBasketSortOption,
  PathPickProductOrderBy
> = {
  recentlyAdded: "recent_basket",
  recentlyRequested: "recent_request",
  recentlyOrdered: "recent_order",
  kreamMarginDesc: "kream_margin_desc",
  poizonMarginDesc: "poizon_margin_desc",
  kreamSalesDesc: "kream_sales_desc",
  poizonSalesDesc: "poizon_sales_desc",
};

// 품절 상품 필터의 `전체` 옵션 값 — 미적용을 뜻하는 센티널이라 쿼리스트링에는 저장하지 않는다.
// (미적용은 is_sold_out 파라미터를 아예 빼는 것과 같고, 담은 상품 조회는 그때 품절도 함께 내려온다)
export const BASKET_SOLD_OUT_ALL_VALUE = "ALL";

// 해외 출고지 확인 기준 — 네이버 주소록에 등록된 패스레이드 MXN 출고지의 우편번호
export const PATH_PICK_OVERSEAS_RELEASE_POSTAL_CODE = "21052";

// 바이어가 스마트스토어 주소록에 등록해야 하는 패스픽 해외 출고지.
// 네이버 주소 입력 폼의 칸과 1:1로 대응하도록 필드 단위로 둔다(칸별 복사 UI가 이 구조를 쓴다).
export const PATH_PICK_OVERSEAS_RELEASE_ADDRESS = {
  name: "패스픽 해외 출고지",
  address1: "Corso Sempione",
  address2: "176",
  city: "Busto Arsizio",
  state: "Varese",
  zipCode: PATH_PICK_OVERSEAS_RELEASE_POSTAL_CODE,
  country: "이탈리아 (IT)",
};

// 칸별 복사 UI가 도는 순서 — 라벨 i18n 키와 값 키를 함께 들고 있다.
export const PATH_PICK_OVERSEAS_RELEASE_ADDRESS_ROWS = [
  { key: "address1", labelKey: "path_pick:basket.overseas_release.address1" },
  { key: "address2", labelKey: "path_pick:basket.overseas_release.address2" },
  { key: "city", labelKey: "path_pick:basket.overseas_release.city" },
  { key: "state", labelKey: "path_pick:basket.overseas_release.state" },
  { key: "zipCode", labelKey: "path_pick:basket.overseas_release.zip_code" },
  { key: "country", labelKey: "path_pick:basket.overseas_release.country" },
] as const satisfies ReadonlyArray<{
  key: keyof Omit<typeof PATH_PICK_OVERSEAS_RELEASE_ADDRESS, "name">;
  labelKey: string;
}>;

// 네이버 스마트스토어 해외상품판매 권한 신청 공식 안내
export const SMARTSTORE_OVERSEAS_SALES_PERMISSION_GUIDE_URL =
  "https://help.sell.smartstore.naver.com/faq/content.help?faqId=3755";

// 네이버 스마트스토어 API 연동 공식 안내
export const SMARTSTORE_API_GUIDE_URL =
  "https://help.sell.smartstore.naver.com/faq/content.help?faqId=3943";

// 네이버 스마트스토어 해외 출고지 등록 공식 안내
export const SMARTSTORE_OVERSEAS_RELEASE_GUIDE_URL =
  "https://help.sell.smartstore.naver.com/faq/content.help?faqId=4031";

// 네이버 스마트스토어 배송비 템플릿 설정 공식 안내
export const SMARTSTORE_DELIVERY_TEMPLATE_GUIDE_URL =
  "https://help.sell.smartstore.naver.com/faq/content.help?faqId=3385";

// 일괄 주문 한도 — 선택 상품을 id__in(GET 쿼리스트링)으로 다시 조회해 주문 모달을 띄우므로,
// 요청 URL 길이가 프록시 한도를 넘지 않게 개수를 묶어 둔다. 요청이 막히면 이 값만 줄인다.
export const BASKET_BULK_ORDER_LIMIT = 500;
